import { useMemo } from 'react';
import { ApolloClient, useQuery } from '@apollo/client';
import {
    GET_ASSETS_QUERY,
    getAssetsQueryVariables,
} from '../graphql/pimcore/modules/resourceLibrary/queries/getAssetsQuery';
import pimcoreClient from '../graphql/pimcore/apollo/pimcoreClient';
import { transformAssetsResponse } from '../graphql/pimcore/modules/resourceLibrary/tools/transformAssetsResponse';
import type { Asset } from '../graphql/pimcore/modules/resourceLibrary/types/Asset';
import { PIMCORE_CONTENT_BASE_URL } from './Pimcore';

export interface UsePimcoreAssetsOptions {
    client?: ApolloClient<unknown>;
    skip?: boolean;
}

export interface UsePimcoreAssetsResult {
    assetsById: Map<string, Asset>;
    loading: boolean;
    error?: unknown;
}

const PIMCORE_ID_REGEX = /pimcore_id="(\d+)"/gi;

type AssetMap = Map<string, Asset>;

export function extractPimcoreAssetIdsFromHtml(html: string | null | undefined): string[] {
    if (!html) {
        return [];
    }

    const ids: string[] = [];
    let match: RegExpExecArray | null;
    while ((match = PIMCORE_ID_REGEX.exec(html)) !== null) {
        const id = match[1];
        if (id && !ids.includes(id)) {
            ids.push(id);
        }
    }
    return ids;
}

function createAssetUrl(asset: Asset): string {
    return `${PIMCORE_CONTENT_BASE_URL}${asset.fullpath}`;
}

function replaceAttributesOnElement(element: Element, assetUrl: string) {
    const tagName = element.tagName.toLowerCase();

    if (tagName === 'a') {
        element.setAttribute('href', assetUrl);
    } else if (element instanceof HTMLImageElement) {
        element.setAttribute('src', assetUrl);
    } else if (element.hasAttribute('href')) {
        element.setAttribute('href', assetUrl);
    } else if (element.hasAttribute('src')) {
        element.setAttribute('src', assetUrl);
    } else {
        element.setAttribute('data-asset-url', assetUrl);
    }

    element.removeAttribute('pimcore_id');
    element.removeAttribute('pimcore_type');
}

export function replacePimcoreAssetPlaceholders(html: string, assetsById: AssetMap): string {
    if (!html || assetsById.size === 0) {
        return html;
    }

    if (typeof window === 'undefined' || !window.document) {
        return html;
    }

    const template = document.createElement('template');
    template.innerHTML = html;

    const placeholders = template.content.querySelectorAll('[pimcore_id]');
    placeholders.forEach((element) => {
        const id = element.getAttribute('pimcore_id');
        if (!id) {
            return;
        }

        const asset = assetsById.get(id);
        if (!asset) {
            return;
        }

        const assetUrl = createAssetUrl(asset);
        replaceAttributesOnElement(element, assetUrl);
    });

    return template.innerHTML;
}

export function usePimcoreAssets(
    assetIds: string[],
    options?: UsePimcoreAssetsOptions
): UsePimcoreAssetsResult {
    const client = options?.client ?? pimcoreClient;
    const skip = options?.skip ?? assetIds.length === 0;

    const { data, loading, error } = useQuery(GET_ASSETS_QUERY, {
        variables: getAssetsQueryVariables(assetIds),
        client,
        skip,
    });

    const assetsById = useMemo(() => {
        if (!data) {
            return new Map<string, Asset>();
        }

        const assets = transformAssetsResponse(data);
        const map = new Map<string, Asset>();
        assets.forEach((asset) => {
            map.set(asset.id, asset);
        });
        return map;
    }, [data]);

    return {
        assetsById,
        loading,
        error,
    };
}

export function collectPimcoreAssetIdsFromHtmlBlocks(
    blocks: (string | null | undefined)[]
): string[] {
    const ids = new Set<string>();
    blocks.forEach((block) => {
        extractPimcoreAssetIdsFromHtml(block).forEach((id) => ids.add(id));
    });
    return Array.from(ids);
}
